/**
 * Paramètres du site : les photos et les avis, modifiables sans déploiement.
 *
 * ## Ce que cet écran remplace
 *
 * Trois photos vivaient en dur dans le code — le bandeau de l'accueil,
 * l'illustration de la connexion, celle du bloc newsletter. Les changer
 * demandait une modification du dépôt, une revue et un déploiement, pour
 * remplacer une photo de branches gelées par des enfants en stage.
 *
 * ## Enregistrer est un geste explicite
 *
 * Téléverser ne publie rien : l'image part dans le seau, l'aperçu change, et
 * c'est « Enregistrer » qui met le site à jour. On peut donc choisir une photo,
 * la regarder, et changer d'avis — sur un écran qui modifie la page d'accueil
 * publique, ça vaut le clic supplémentaire.
 *
 * ## Les avis sont saisis, pas récupérés
 *
 * L'API Places de Google ne rend que cinq avis, « les plus pertinents » ou
 * « les plus récents », sans qu'on puisse choisir lesquels — et ses conditions
 * interdisent de les stocker. « Nos meilleurs avis » passe donc par une saisie,
 * avec le lien vers la fiche Google à côté pour que le lecteur vérifie.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Check, Image as Icone, Loader2, Lock, Plus, Star, Trash2, Upload } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore';
import {
  PAR_DEFAUT,
  type AvisGoogle,
  type CleReglage,
  type Photo,
  type Reglages,
  type Temoignage,
} from '../../lib/reglages';
import { ecrireReglage, lireReglages, televerserImage } from '../../lib/reglages/gateway.supabase';
import { MODES, type ModeAvoirAuto } from '../../lib/finances/avoirAuto';
import { ecrireProvisionAuto, lireProvisionAuto } from '../../lib/finances/provision.supabase';

/** Les trois photos, avec ce qu'elles sont et où elles s'affichent. */
const PHOTOS: { cle: CleReglage; titre: string; ou: string }[] = [
  {
    cle: 'image_accueil',
    titre: 'Bandeau de la page d’accueil',
    ou: 'En haut de la page d’accueil publique, à côté du titre.',
  },
  {
    cle: 'image_connexion',
    titre: 'Page de connexion et d’inscription',
    ou: 'À gauche du formulaire de connexion.',
  },
  {
    cle: 'image_newsletter',
    titre: 'Bloc newsletter',
    ou: 'En bas de la page d’accueil, à côté de l’invitation à s’abonner.',
  },
];

/* -------------------------------------------------------------------------- */
/* Une photo                                                                   */
/* -------------------------------------------------------------------------- */

const CartePhoto = ({
  cle,
  titre,
  ou,
  valeur,
  onEnregistrer,
}: {
  cle: CleReglage;
  titre: string;
  ou: string;
  valeur: Photo;
  onEnregistrer: (cle: CleReglage, photo: Photo) => Promise<void>;
}) => {
  const [brouillon, setBrouillon] = useState<Photo>(valeur);
  const [televersement, setTeleversement] = useState(false);
  const [enregistrement, setEnregistrement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => setBrouillon(valeur), [valeur]);

  const modifie = brouillon.url !== valeur.url || brouillon.alt !== valeur.alt;

  const choisir = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fichier = e.target.files?.[0];
    /* Sans cette remise à zéro, rechoisir le même fichier ne déclenche rien. */
    e.target.value = '';
    if (!fichier) return;

    setErreur(null);
    setTeleversement(true);
    try {
      const url = await televerserImage(fichier, cle);
      setBrouillon((b) => ({ ...b, url }));
    } catch (err) {
      setErreur(err instanceof Error ? err.message : 'L’image n’a pas pu être envoyée.');
    } finally {
      setTeleversement(false);
    }
  };

  const enregistrer = async () => {
    if (!brouillon.url.trim()) {
      setErreur('Une adresse d’image est nécessaire.');
      return;
    }
    if (!brouillon.alt.trim()) {
      setErreur('La description est nécessaire : sans elle, l’image n’existe pas pour un lecteur d’écran.');
      return;
    }
    setErreur(null);
    setEnregistrement(true);
    try {
      await onEnregistrer(cle, { url: brouillon.url.trim(), alt: brouillon.alt.trim() });
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <section className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
      <h3 className="text-base font-semibold text-gray-900">{titre}</h3>
      <p className="mt-0.5 text-sm text-gray-500">{ou}</p>

      <div className="mt-4 grid gap-5 sm:grid-cols-[14rem,1fr]">
        <div className="aspect-[4/3] overflow-hidden rounded-lg bg-gray-100 ring-1 ring-gray-200">
          {brouillon.url ? (
            <img src={brouillon.url} alt="" className="h-full w-full object-cover" />
          ) : (
            <Icone className="h-full w-full p-12 text-gray-300" aria-hidden="true" />
          )}
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <label className="btn-outline inline-flex min-h-[44px] cursor-pointer items-center">
              {televersement ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {televersement ? 'Envoi…' : 'Choisir une image'}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={choisir}
                disabled={televersement}
                className="hidden"
              />
            </label>
            <span className="text-xs text-gray-500">JPG, PNG ou WebP · 5 Mo maximum</span>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Adresse de l’image
            </label>
            <input
              type="url"
              value={brouillon.url}
              onChange={(e) => setBrouillon((b) => ({ ...b, url: e.target.value }))}
              className="form-input"
              placeholder="https://…"
            />
            <p className="mt-1 text-xs text-gray-500">
              Remplie par le téléversement, ou collée à la main pour une image hébergée ailleurs.
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Ce que montre l’image
            </label>
            <input
              type="text"
              value={brouillon.alt}
              onChange={(e) => setBrouillon((b) => ({ ...b, alt: e.target.value }))}
              className="form-input"
              placeholder="Des enfants qui jardinent pendant un stage"
            />
            <p className="mt-1 text-xs text-gray-500">
              Lu à voix haute par les lecteurs d’écran, et lu par Google.
            </p>
          </div>

          {erreur && (
            <p className="flex items-start gap-1.5 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              {erreur}
            </p>
          )}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={enregistrer}
              disabled={!modifie || enregistrement || televersement}
              className="btn-primary inline-flex min-h-[44px] items-center disabled:opacity-50"
            >
              {enregistrement ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Check className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              Enregistrer
            </button>
            {modifie && !enregistrement && (
              <button
                type="button"
                onClick={() => {
                  setBrouillon(valeur);
                  setErreur(null);
                }}
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Annuler
              </button>
            )}
            {!modifie && <span className="text-sm text-gray-400">Aucune modification</span>}
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/* Les avis                                                                    */
/* -------------------------------------------------------------------------- */

const CarteAvis = ({
  valeur,
  onEnregistrer,
}: {
  valeur: AvisGoogle;
  onEnregistrer: (cle: CleReglage, avis: AvisGoogle) => Promise<void>;
}) => {
  const [brouillon, setBrouillon] = useState<AvisGoogle>(valeur);
  const [enregistrement, setEnregistrement] = useState(false);

  useEffect(() => setBrouillon(valeur), [valeur]);

  const modifie = JSON.stringify(brouillon) !== JSON.stringify(valeur);

  const majTemoignage = (i: number, champs: Partial<Temoignage>) =>
    setBrouillon((b) => ({
      ...b,
      temoignages: b.temoignages.map((t, j) => (j === i ? { ...t, ...champs } : t)),
    }));

  const enregistrer = async () => {
    setEnregistrement(true);
    try {
      /*
        Les témoignages vides sont écartés ici plutôt que refusés : un
        administrateur qui ajoute une ligne puis change d'avis ne doit pas avoir
        à la supprimer pour pouvoir enregistrer le reste.
      */
      await onEnregistrer('avis_google', {
        ...brouillon,
        lien: brouillon.lien.trim(),
        temoignages: brouillon.temoignages.filter((t) => t.auteur.trim() && t.texte.trim()),
      });
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <section className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
      <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900">
        <Star className="h-4 w-4 text-amber-500" aria-hidden="true" />
        Avis Google
      </h3>
      <p className="mt-0.5 text-sm text-gray-500">
        Affichés sur la page d’accueil publique. Choisissez-les vous-même&nbsp;: l’API de Google ne
        rend que cinq avis, sans qu’on puisse dire lesquels.
      </p>

      <div className="mt-4 space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Lien vers votre fiche Google
          </label>
          <input
            type="url"
            value={brouillon.lien}
            onChange={(e) => setBrouillon((b) => ({ ...b, lien: e.target.value }))}
            className="form-input"
            placeholder="https://www.google.com/search?q=…"
          />
          <p className="mt-1 text-xs text-gray-500">
            « Voir tous les avis sur Google » y mène. C’est ce lien qui rend la sélection honnête.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Note globale
              <span className="ml-2 text-xs font-normal text-gray-400">facultatif</span>
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="5"
              value={brouillon.note ?? ''}
              onChange={(e) =>
                setBrouillon((b) => ({
                  ...b,
                  note: e.target.value === '' ? null : Number(e.target.value),
                }))
              }
              className="form-input"
              placeholder="4,8"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Nombre d’avis
              <span className="ml-2 text-xs font-normal text-gray-400">facultatif</span>
            </label>
            <input
              type="number"
              min="0"
              value={brouillon.nombre ?? ''}
              onChange={(e) =>
                setBrouillon((b) => ({
                  ...b,
                  nombre: e.target.value === '' ? null : Number(e.target.value),
                }))
              }
              className="form-input"
              placeholder="42"
            />
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-900">
              Les avis mis en avant ({brouillon.temoignages.length})
            </h4>
            <button
              type="button"
              onClick={() =>
                setBrouillon((b) => ({
                  ...b,
                  temoignages: [...b.temoignages, { auteur: '', texte: '', date: '' }],
                }))
              }
              className="inline-flex min-h-[44px] items-center text-sm font-medium text-primary-700 hover:text-primary-800"
            >
              <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
              Ajouter un avis
            </button>
          </div>

          {brouillon.temoignages.length === 0 ? (
            <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
              Aucun avis pour l’instant — la section reste masquée sur la page d’accueil tant qu’il
              n’y en a pas.
            </p>
          ) : (
            <ul className="space-y-3">
              {brouillon.temoignages.map((t, i) => (
                <li key={i} className="rounded-lg bg-gray-50 p-4 ring-1 ring-gray-200">
                  <div className="flex gap-3">
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="grid gap-2 sm:grid-cols-[1fr,10rem]">
                        <input
                          type="text"
                          value={t.auteur}
                          onChange={(e) => majTemoignage(i, { auteur: e.target.value })}
                          className="form-input"
                          placeholder="Prénom de l’auteur"
                        />
                        <input
                          type="date"
                          value={t.date ?? ''}
                          onChange={(e) => majTemoignage(i, { date: e.target.value })}
                          className="form-input"
                        />
                      </div>
                      <textarea
                        value={t.texte}
                        onChange={(e) => majTemoignage(i, { texte: e.target.value })}
                        rows={3}
                        className="form-input"
                        placeholder="Le texte de l’avis, tel qu’il a été écrit."
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setBrouillon((b) => ({
                          ...b,
                          temoignages: b.temoignages.filter((_, j) => j !== i),
                        }))
                      }
                      aria-label={`Retirer l’avis de ${t.auteur || 'cet auteur'}`}
                      className="h-10 w-10 flex-shrink-0 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="mx-auto h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={enregistrer}
            disabled={!modifie || enregistrement}
            className="btn-primary inline-flex min-h-[44px] items-center disabled:opacity-50"
          >
            {enregistrement ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Check className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            Enregistrer les avis
          </button>
          {modifie && !enregistrement && (
            <button
              type="button"
              onClick={() => setBrouillon(valeur)}
              className="text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              Annuler
            </button>
          )}
          {!modifie && <span className="text-sm text-gray-400">Aucune modification</span>}
        </div>
      </div>
    </section>
  );
};


/* -------------------------------------------------------------------------- */
/* L'usage automatique des avoirs                                              */
/* -------------------------------------------------------------------------- */

/**
 * L'interrupteur qui décide si l'avoir d'une famille peut payer d'office.
 *
 * Il ne vit pas dans la même table par hasard : c'est le seul endroit que
 * l'équipe connaît déjà pour changer quelque chose sans déploiement. Mais ce
 * n'est pas un réglage d'affichage — il ouvre ou ferme deux portes par
 * lesquelles de l'argent circule, et l'écran doit le dire avant le clic.
 *
 * La règle elle-même tient en base : `v_user_provision_balances` ne rend un
 * avoir utilisable que si ce réglage l'autorise, et `quote_order` comme
 * `demander_remboursement` en dépendent. Cet écran ne fait que le poser.
 */
const CarteAvoirs: React.FC<{
  mode: ModeAvoirAuto;
  onChanger: (mode: ModeAvoirAuto) => Promise<void>;
}> = ({ mode, onChanger }) => {
  const [enCours, setEnCours] = useState<ModeAvoirAuto | null>(null);

  const choisir = async (m: ModeAvoirAuto) => {
    if (m === mode) return;
    setEnCours(m);
    try {
      await onChanger(m);
    } finally {
      setEnCours(null);
    }
  };

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
        <Lock className="h-5 w-5 text-primary-600" aria-hidden="true" />
        Avoirs des familles
      </h2>
      <p className="mt-1 text-sm text-gray-600">
        Un avoir est de l’argent déjà reçu qui n’est plus réclamé — un trop-perçu, une note de
        crédit. Ce réglage décide s’il peut <strong>payer d’office une nouvelle inscription</strong>{' '}
        et si une famille peut <strong>en demander le remboursement depuis son espace</strong>.
      </p>
      <p className="mt-2 text-sm text-gray-600">
        Il ne change rien au reste : l’équipe continue d’émettre des notes de crédit, de placer des
        paiements, de corriger des rapprochements et d’enregistrer une demande reçue par courriel.
        Aucun avoir n’est effacé, et tous restent visibles.
      </p>

      <fieldset className="mt-4 space-y-2">
        <legend className="sr-only">Usage automatique des avoirs</legend>
        {MODES.map((m) => (
          <label
            key={m.mode}
            className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 ${
              mode === m.mode ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <input
              type="radio"
              name="provision-auto"
              className="mt-1"
              checked={mode === m.mode}
              disabled={enCours !== null}
              onChange={() => void choisir(m.mode)}
            />
            <span>
              <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                {m.titre}
                {enCours === m.mode && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                {mode === m.mode && enCours === null && (
                  <Check className="h-4 w-4 text-primary-600" aria-hidden="true" />
                )}
              </span>
              <span className="mt-0.5 block text-sm text-gray-600">{m.detail}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {mode === 'on' && (
        <p className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Toutes les familles utilisent leur avoir, vérifié ou non. À réserver au moment où
            l’historique bancaire sera complet et rapproché.
          </span>
        </p>
      )}
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/* La page                                                                     */
/* -------------------------------------------------------------------------- */

const AdminReglagesSitePage = () => {
  const { user } = useAuthStore();
  const [reglages, setReglages] = useState<Reglages>(PAR_DEFAUT);
  /* `off` en attendant la lecture : si la requête échoue, l'écran annonce le
     verrou plutôt qu'une ouverture. Le même réflexe que `versMode`. */
  const [modeAvoirs, setModeAvoirs] = useState<ModeAvoirAuto>('off');
  const [chargement, setChargement] = useState(true);

  const charger = useCallback(async () => {
    setChargement(true);
    const [r, m] = await Promise.all([lireReglages(), lireProvisionAuto()]);
    setReglages(r);
    setModeAvoirs(m);
    setChargement(false);
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const enregistrer = async (cle: CleReglage, valeur: unknown) => {
    try {
      await ecrireReglage(cle, valeur, user?.id ?? null);
      /*
        On relit plutôt que de recopier le brouillon : ce qui compte est ce que
        la base a réellement gardé. Une RLS qui refuse en silence — l'écriture
        aboutit, zéro ligne touchée — se verrait immédiatement ici.
      */
      await charger();
      toast.success('Enregistré. Le site est à jour.');
    } catch (err) {
      console.error('Réglage non enregistré :', err);
      toast.error(
        err instanceof Error ? err.message : 'L’enregistrement a échoué. Réessayez.',
      );
    }
  };

  const changerModeAvoirs = async (mode: ModeAvoirAuto) => {
    try {
      await ecrireProvisionAuto(mode, user?.id ?? null);
      /* On relit : ce qui compte est ce que la base a gardé, pas ce qu'on a
         envoyé. Une RLS qui refuse en silence se verrait ici. */
      await charger();
      toast.success('Enregistré.');
    } catch (err) {
      console.error('Usage des avoirs non enregistré :', err);
      toast.error(err instanceof Error ? err.message : 'L’enregistrement a échoué. Réessayez.');
      await charger();
    }
  };

  if (chargement) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" role="status">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" aria-hidden="true" />
        <span className="sr-only">Chargement des paramètres…</span>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Paramètres du site</h1>
        <p className="mt-1 text-gray-600">
          Les photos et les avis affichés sur les pages publiques, et l’usage des avoirs des
          familles. Les changements prennent effet immédiatement, sans déploiement.
        </p>
      </header>

      <div className="space-y-6">
        {PHOTOS.map((p) => (
          <CartePhoto
            key={p.cle}
            cle={p.cle}
            titre={p.titre}
            ou={p.ou}
            valeur={reglages[p.cle] as Photo}
            onEnregistrer={enregistrer}
          />
        ))}

        <CarteAvis valeur={reglages.avis_google} onEnregistrer={enregistrer} />

        <CarteAvoirs mode={modeAvoirs} onChanger={changerModeAvoirs} />
      </div>

      <Toaster position="top-right" />
    </div>
  );
};

export default AdminReglagesSitePage;
